import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiMessageSquare, FiX, FiSend, FiSearch, FiCheck,
    FiArrowLeft, FiVolume2, FiVolumeX, FiPlus, FiImage,
    FiFile, FiLink, FiPackage, FiDownload, FiExternalLink,
    FiMaximize2,
    FiMove,
    FiMinimize2,
    FiUsers,
} from 'react-icons/fi';
import { HiMiniUserGroup } from "react-icons/hi2";
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../config/supabase';
import {
    getConversations, getOrCreateConversation, createGroupConversation, getMessages, sendMessage,
    markMessagesAsRead, deleteConversation, getAvailableUsers,
    subscribeToConversationMessages, subscribeToMessages, sendSystemMessage, uploadAttachment, getAttachmentSignedUrl,
    searchJobsForLink, getConversationMembers, addGroupMembers, updateGroupMemberRole, removeGroupMember, transferGroupOwnership
} from '../../lib/supabase/messages';
import messageSound from '../../lib/soundUtils/messageSound';
import MessageReactions from './MessageReactions';

// ─── Helpers ────────────────────────────────────────────────────────────────

const DEFAULT_TITLE = 'Notify';
const MESSAGE_PANEL_STORAGE_KEY = 'notify_message_panel_state';

const updateBrowserMessageBadge = async (count) => {
    document.title = count > 0 ? `(${count}) ${DEFAULT_TITLE}` : DEFAULT_TITLE;

    try {
        if ('setAppBadge' in navigator) {
            if (count > 0) {
                await navigator.setAppBadge(count);
            } else if ('clearAppBadge' in navigator) {
                await navigator.clearAppBadge();
            }
        }
    } catch (error) {
        console.warn('App badge update failed:', error);
    }
};

function isSameDay(a, b) {
    const da = new Date(a), db = new Date(b);
    return (
        da.getFullYear() === db.getFullYear() &&
        da.getMonth() === db.getMonth() &&
        da.getDate() === db.getDate()
    );
}

function getDateLabel(timestamp) {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (isSameDay(date, today)) return 'Today';
    if (isSameDay(date, yesterday)) return 'Yesterday';
    const sameYear = date.getFullYear() === today.getFullYear();
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        ...(sameYear ? {} : { year: 'numeric' }),
    });
}

function formatTimestamp(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMessageTime(timestamp) {
    return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}

function formatFileSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// File types the browser can show in a tab instead of downloading
function isPreviewableFile(attachment) {
    const mimeType = attachment?.mimeType || '';
    if (mimeType === 'application/pdf' || /\.pdf$/i.test(attachment?.name || '')) return true;
    return /^(image|video|audio)\//.test(mimeType) || mimeType === 'text/plain';
}

function getDisplayName(user) {
    if (!user) return 'Unknown';
    if (user.role === 'system' || user.username === 'Notify Team') return 'Notify Team';
    return user.full_name || user.username || 'Unknown';
}

function capitalizeFirstLetter(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function getRoleBadgeColor(role) {
    switch (role) {
        case 'super_admin':
        case 'admin':
            return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
        case 'designer':
            return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
        case 'estimator':
            return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
        case 'driver':
            return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
        default:
            return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300';
    }
}

function getInitialAvatar(user, size = 'md') {
    const isSystem = user?.role === 'system' || user?.username === 'Notify Team';
    const gradient = isSystem
        ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
        : 'bg-gradient-to-br from-gray-400 to-zinc-600';
    const dims = size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-14 h-14 text-lg' : 'w-12 h-12 text-base';
    return (
        <div className={`${dims} ${gradient} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0`}>
            {getDisplayName(user).charAt(0).toUpperCase()}
        </div>
    );
}

function getConversationTitle(conversation) {
    if (!conversation) return 'Messages';

    if (conversation.type === 'group') {
        return conversation.title || 'Group Chat';
    }

    return getDisplayName(conversation.otherUser);
}

function getMessageSender(message, selectedConversation) {
    if (message.sender) return message.sender;
    return selectedConversation?.otherUser;
}

const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

const getFirstUrlFromText = (text = '') => {
    const match = text.match(URL_REGEX);
    if (!match?.[0]) return null;

    return match[0].startsWith('http')
        ? match[0]
        : `https://${match[0]}`;
};

const MessageTextWithLinks = ({ text, isOwn }) => {
    if (!text) return null;

    const parts = text.split(URL_REGEX);

    return (
        <p className="text-sm leading-relaxed break-words whitespace-pre-line">
            {parts.map((part, index) => {
                const isUrl = part.match(URL_REGEX);

                if (!isUrl) {
                    return <React.Fragment key={index}>{part}</React.Fragment>;
                }

                const cleanUrl = part.replace(/[),.!?]+$/, '');
                const trailing = part.slice(cleanUrl.length);
                const href = cleanUrl.startsWith('http')
                    ? cleanUrl
                    : `https://${cleanUrl}`;

                return (
                    <React.Fragment key={index}>
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={`underline underline-offset-2 break-all font-medium ${isOwn
                                ? 'text-blue-700 hover:text-blue-900'
                                : 'text-blue-600 dark:text-blue-300 hover:text-blue-800'
                                }`}
                        >
                            {cleanUrl}
                        </a>
                        {trailing}
                    </React.Fragment>
                );
            })}
        </p>
    );
};

const UrlPreviewCard = ({ text }) => {
    const [preview, setPreview] = useState(null);
    const [failed, setFailed] = useState(false);

    const url = getFirstUrlFromText(text);

    useEffect(() => {
        if (!url) return;

        let cancelled = false;

        const loadPreview = async () => {
            try {
                const { data, error } = await supabase.functions.invoke('get-url-preview', {
                    body: { url },
                });

                console.log(data)

                if (error) throw error;
                if (!cancelled && data?.title) {
                    setPreview(data);
                }
            } catch {
                if (!cancelled) setFailed(true);
            }
        };

        loadPreview();

        return () => {
            cancelled = true;
        };
    }, [url]);

    if (!url || failed || !preview?.title) return null;

    return (
        <a
            href={preview.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="mt-2 block overflow-hidden rounded-xl border border-gray-200 bg-white/80 hover:bg-white transition-colors max-w-[260px]"
        >
            {preview.image && (
                <img
                    src={preview.image}
                    alt=""
                    className="w-full h-28 object-cover bg-gray-100"
                    loading="lazy"
                />
            )}

            <div className="p-2.5">
                <p className="text-[11px] text-gray-400 font-medium truncate">
                    {preview.domain}
                </p>

                <p className="text-xs font-semibold text-gray-900 line-clamp-2">
                    {preview.title}
                </p>

                {preview.description && (
                    <p className="text-[11px] text-gray-500 mt-1 line-clamp-2">
                        {preview.description}
                    </p>
                )}
            </div>
        </a>
    );
};



// ─── AttachmentMessage ───────────────────────────────────────────────────────

const AttachmentMessage = ({ attachment, navigate, onImageClick, isOwn }) => {
    // Resolve signed URL for private storage attachments (image, gif, file)
    const [resolvedUrl, setResolvedUrl] = useState(null);
    // Files only: URL that downloads under the original name (the stored path is randomised)
    const [downloadUrl, setDownloadUrl] = useState(null);

    useEffect(() => {
        if (!attachment) return;
        if (attachment.path) {
            const isFile = attachment.type === 'file';
            if (isFile && attachment.name) {
                getAttachmentSignedUrl(attachment.path, 3600, attachment.name)
                    .then(url => {
                        setDownloadUrl(url);
                        // Nothing to preview — clicking the file downloads it
                        if (!isPreviewableFile(attachment)) setResolvedUrl(url);
                    })
                    .catch(err => console.error('Signed URL error:', err));
            }
            if (!isFile || !attachment.name || isPreviewableFile(attachment)) {
                getAttachmentSignedUrl(attachment.path)
                    .then(setResolvedUrl)
                    .catch(err => console.error('Signed URL error:', err));
            }
        } else if (attachment.url) {
            // External URL (type: 'url') — use as-is
            setResolvedUrl(attachment.url);
        }
    }, [attachment?.path, attachment?.url, attachment?.type, attachment?.name, attachment?.mimeType]);

    if (!attachment) return null;

    const base = isOwn
        ? 'bg-blue-800/60 text-white'
        : 'bg-gray-200 dark:bg-zinc-600 text-gray-900 dark:text-white';

    switch (attachment.type) {
        case 'image':
        case 'gif':
            return (
                <div className="mt-1 max-w-[220px]">
                    {resolvedUrl ? (
                        <img
                            src={resolvedUrl}
                            alt={attachment.name || 'image'}
                            className="rounded-xl object-cover w-full cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => onImageClick?.(resolvedUrl)}
                        />
                    ) : (
                        <div className="w-32 h-20 rounded-xl bg-white/20 flex items-center justify-center">
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin opacity-50" />
                        </div>
                    )}
                </div>
            );

        case 'file':
            return (
                <div
                    className={`flex items-center gap-2.5 mt-1 px-3 py-2.5 rounded-xl transition-colors ${base} ${!resolvedUrl ? 'opacity-60 pointer-events-none' : ''}`}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Opens previewable files (e.g. PDF) in a new tab; downloads the rest */}
                    <a
                        href={resolvedUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2.5 flex-1 min-w-0"
                    >
                        <div className="flex-shrink-0 w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
                            <FiFile className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold truncate">{attachment.name || 'File'}</p>
                            {attachment.size && (
                                <p className="text-xs opacity-70">{formatFileSize(attachment.size)}</p>
                            )}
                        </div>
                    </a>
                    {resolvedUrl ? (
                        <a
                            href={downloadUrl || resolvedUrl}
                            download={attachment.name}
                            title="Download"
                            className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                        >
                            <FiDownload className="w-4 h-4" />
                        </a>
                    ) : (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin opacity-40" />
                    )}
                </div>
            );

        case 'url':
            return (
                <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 mt-1 text-current underline-offset-2 hover:underline text-xs opacity-90 break-all"
                    onClick={e => e.stopPropagation()}
                >
                    <FiExternalLink className="w-3 h-3 flex-shrink-0" />
                    <span className="max-w-[200px] truncate">{attachment.url}</span>
                </a>
            );

        case 'job':
            return (
                <button
                    onClick={() => navigate(`/${attachment.companySlug}/assignment/${attachment.jobId}`)}
                    className={`flex items-center gap-2.5 mt-1 px-3 py-2.5 rounded-xl transition-colors w-full text-left ${base}`}
                >
                    <div className="flex-shrink-0 w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
                        <FiPackage className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate">Job #{attachment.jobUniqueId}</p>
                        {attachment.jobAddress && (
                            <p className="text-xs opacity-70 flex-wrap whitespace-pre-line max-w-36">{attachment.jobAddress}</p>
                        )}
                    </div>
                    <FiExternalLink className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
                </button>
            );

        default:
            return null;
    }
};

// ─── DoubleCheck component ───────────────────────────────────────────────────

const DoubleCheck = ({ isRead }) => (
    <div className="flex">
        <FiCheck className={`w-3 h-3 ${isRead ? 'text-blue-400' : 'text-gray-400'}`} />
        <FiCheck className={`w-3 h-3 -ml-1.5 ${isRead ? 'text-blue-400' : 'text-gray-400'}`} />
    </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const MessageMenu = ({
    isOpen,
    onOpen,
    onClose,
    onRefreshBadge,
    isSidebarOpen,
    pageMode = false,
}) => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();

    // Core state
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(0.7);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);

    // New conversation
    const [showNewConversation, setShowNewConversation] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [searchUsers, setSearchUsers] = useState('');

    const [newConversationMode, setNewConversationMode] = useState('direct'); // direct | group
    const [groupTitle, setGroupTitle] = useState('');
    const [selectedGroupMemberIds, setSelectedGroupMemberIds] = useState([]);

    const [showGroupDetails, setShowGroupDetails] = useState(false);
    const [groupMembers, setGroupMembers] = useState([]);
    const [loadingGroupMembers, setLoadingGroupMembers] = useState(false);
    const [selectedAddMemberIds, setSelectedAddMemberIds] = useState([]);

    const [isDetached, setIsDetached] = useState(false);
    const [panelPosition, setPanelPosition] = useState({ x: 420, y: 120 });
    const [panelSize, setPanelSize] = useState({ width: 520, height: 680 });
    const [isDraggingPanel, setIsDraggingPanel] = useState(false);

    const dragStartRef = useRef(null);

    // Attachment state
    const [showAttachmentPicker, setShowAttachmentPicker] = useState(false);
    const [attachmentMode, setAttachmentMode] = useState(null); // 'url' | 'job' | null
    const [pendingAttachment, setPendingAttachment] = useState(null);
    const [urlInput, setUrlInput] = useState('');
    const [jobSearchQuery, setJobSearchQuery] = useState('');
    const [jobSearchResults, setJobSearchResults] = useState([]);
    const [jobSearchLoading, setJobSearchLoading] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [fileInputAccept, setFileInputAccept] = useState('*/*');
    const [fileInputType, setFileInputType] = useState('file');

    // Image lightbox
    const [lightboxImage, setLightboxImage] = useState(null);

    // Refs
    const menuRef = useRef(null);
    const messagesEndRef = useRef(null);
    const messageInputRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const conversationSubscriptionRef = useRef(null);
    const fileInputRef = useRef(null);
    // Scroll intent flags — set before calling setMessages so the effect knows what to do
    const isInitialLoadRef = useRef(false); // jump to bottom instantly
    const isLoadMoreRef = useRef(false);    // prepend — handleScroll restores position, don't interfere
    const globalMessagesSubscriptionRef = useRef(null);
    const loadingMoreMessagesRef = useRef(false);

    const [replyingTo, setReplyingTo] = useState(null);

    // ── Effects ──────────────────────────────────────────────────────────────

    useEffect(() => {
        const savedVolume = localStorage.getItem('messageVolume');
        const savedMuted = localStorage.getItem('messageMuted');
        if (savedVolume) setVolume(parseFloat(savedVolume));
        if (savedMuted) setIsMuted(savedMuted === 'true');
    }, []);

    useEffect(() => {
        if ((isOpen || pageMode) && userProfile?.id && (company?.id || userProfile?.company_id)) {
            fetchConversations();
            if (onRefreshBadge) onRefreshBadge();
        }
    }, [isOpen, userProfile, company]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(MESSAGE_PANEL_STORAGE_KEY) || '{}');

            if (saved?.isDetached && window.innerWidth >= 768) {
                setIsDetached(true);

                if (saved.panelSize) {
                    setPanelSize(saved.panelSize);
                }

                if (saved.panelPosition) {
                    setPanelPosition(saved.panelPosition);
                }
            }
        } catch (error) {
            console.warn('Failed to load message panel state:', error);
        }
    }, []);

    useEffect(() => {
        if (pageMode) return;

        localStorage.setItem(
            MESSAGE_PANEL_STORAGE_KEY,
            JSON.stringify({
                isDetached,
                panelPosition,
                panelSize,
            })
        );
    }, [isDetached, panelPosition, panelSize, pageMode]);

    // Scroll after messages array changes — three distinct cases
    useEffect(() => {
        if (isLoadMoreRef.current) {
            isLoadMoreRef.current = false;
            return;
        }

        const scrollToBottom = (behavior = 'auto') => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const el = messagesContainerRef.current;
                    if (!el) return;

                    el.scrollTo({
                        top: el.scrollHeight,
                        behavior,
                    });
                });
            });
        };

        if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            scrollToBottom('auto');
            return;
        }

        scrollToBottom('smooth');
    }, [messages.length]);

    // Focus input when conversation is selected
    useEffect(() => {
        if (selectedConversation && messageInputRef.current) {
            messageInputRef.current.focus();
        }
    }, [selectedConversation]);

    // Real-time subscription for current conversation
    useEffect(() => {
        if (!selectedConversation?.id) {
            if (conversationSubscriptionRef.current) {
                supabase.removeChannel(conversationSubscriptionRef.current);
                conversationSubscriptionRef.current = null;
            }
            return;
        }

        if (conversationSubscriptionRef.current) {
            supabase.removeChannel(conversationSubscriptionRef.current);
        }

        const subscription = subscribeToConversationMessages(
            selectedConversation.id,
            (newMessage) => {
                if (newMessage.sender_id === userProfile.id) return;

                setMessages(prev => {
                    if (prev.some(m => m.id === newMessage.id)) return prev;
                    return [...prev, newMessage];
                });
                if (selectedConversation?.id === newMessage.conversation_id) {
                    bumpConversationToTop(selectedConversation.id, newMessage);
                }

                if (newMessage.sender_id !== userProfile.id) {
                    markMessagesAsRead(selectedConversation.id, userProfile.id);
                    fetchConversations();
                    if (onRefreshBadge) onRefreshBadge();
                }
            }
        );

        conversationSubscriptionRef.current = subscription;

        return () => {
            if (conversationSubscriptionRef.current) {
                supabase.removeChannel(conversationSubscriptionRef.current);
                conversationSubscriptionRef.current = null;
            }
        };
    }, [selectedConversation?.id, userProfile?.id]); // eslint-disable-line react-hooks/exhaustive-deps


    const restoreSavedPanelState = () => {
        if (pageMode || window.innerWidth < 768) return;

        try {
            const saved = JSON.parse(localStorage.getItem(MESSAGE_PANEL_STORAGE_KEY) || '{}');

            if (saved?.isDetached) {
                const savedSize = saved.panelSize || {};
                const width = savedSize.width || Math.min(560, window.innerWidth - 24);
                const height = savedSize.height || Math.min(720, window.innerHeight - 24);

                setIsDetached(true);
                setPanelSize({ width, height });

                if (saved.panelPosition) {
                    setPanelPosition(
                        clampPanelPosition(saved.panelPosition.x, saved.panelPosition.y, width, height)
                    );
                }
            }
        } catch (error) {
            console.warn('Failed to restore message panel state:', error);
        }
    };

    const openIncomingConversation = async (conversationId) => {
        if (pageMode) return;

        restoreSavedPanelState();

        if (onOpen) {
            onOpen();
        }

        const companyId = company?.id || userProfile?.company_id;
        if (!userProfile?.id || !companyId) return;

        const data = await getConversations(userProfile.id, companyId);
        setConversations(data);

        const conversation = data.find(conv => conv.id === conversationId);
        if (!conversation) return;

        setSelectedConversation(conversation);
        setHasMoreMessages(true);
        setPendingAttachment(null);
        setShowAttachmentPicker(false);
        setAttachmentMode(null);
        setShowNewConversation(false);
        setShowGroupDetails(false);

        await fetchMessages(conversation.id);

        if (onRefreshBadge) onRefreshBadge();
    };

    useEffect(() => {
        const companyId = company?.id || userProfile?.company_id;
        if (!userProfile?.id || !companyId) return;

        if (globalMessagesSubscriptionRef.current) {
            supabase.removeChannel(globalMessagesSubscriptionRef.current);
        }

        const subscription = subscribeToMessages(userProfile.id, async (newMessage) => {
            if (newMessage.sender_id === userProfile.id) return;

            // if (!isMuted) {
            //     messageSound.play(volume);
            // }

            setConversations(prev => {
                const found = prev.find(conv => conv.id === newMessage.conversation_id);

                if (!found) {
                    fetchConversations();
                    return prev;
                }

                const updated = {
                    ...found,
                    lastMessage: newMessage,
                    updated_at: newMessage.created_at || new Date().toISOString(),
                    last_message_at: newMessage.created_at || new Date().toISOString(),
                    unreadCount:
                        selectedConversation?.id === newMessage.conversation_id
                            ? found.unreadCount || 0
                            : (found.unreadCount || 0) + 1,
                };

                return [
                    updated,
                    ...prev.filter(conv => conv.id !== newMessage.conversation_id),
                ];
            });

            await openIncomingConversation(newMessage.conversation_id);
        });

        globalMessagesSubscriptionRef.current = subscription;

        return () => {
            if (globalMessagesSubscriptionRef.current) {
                supabase.removeChannel(globalMessagesSubscriptionRef.current);
                globalMessagesSubscriptionRef.current = null;
            }
        };
    }, [
        userProfile?.id,
        company?.id,
        userProfile?.company_id,
        selectedConversation?.id,
        isMuted,
        volume,
    ]);

    const unreadCount = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

    useEffect(() => {
        updateBrowserMessageBadge(unreadCount);
    }, [unreadCount]);

    // Auto-resize textarea
    const autoResizeTextarea = (el) => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 128) + 'px';
    };

    // ── Data fetching ─────────────────────────────────────────────────────────

    const fetchConversations = async () => {
        try {
            const companyId = company?.id || userProfile?.company_id;

            if (!userProfile?.id || !companyId) return;

            const data = await getConversations(userProfile.id, companyId);
            setConversations(data);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        }
    };

    const PAGE_SIZE = 15;

    const fetchMessages = async (conversationId, loadMore = false) => {
        try {
            if (loadMore) {
                if (loadingMoreMessagesRef.current) return;

                loadingMoreMessagesRef.current = true;
                setLoadingMoreMessages(true);

                const before = messages[0]?.created_at ?? null;
                if (!before) {
                    setHasMoreMessages(false);
                    return;
                }

                const data = await getMessages(conversationId, PAGE_SIZE, before, userProfile.id);

                if (data.length < PAGE_SIZE) setHasMoreMessages(false);

                isLoadMoreRef.current = true;

                setMessages(prev => {
                    const existingIds = new Set(prev.map(m => m.id));
                    const uniqueOlder = data.filter(m => !existingIds.has(m.id));
                    return [...uniqueOlder, ...prev];
                });
            } else {
                const data = await getMessages(conversationId, PAGE_SIZE, null, userProfile.id);

                isInitialLoadRef.current = true;

                setMessages(() => {
                    const seen = new Set();
                    return data.filter(m => {
                        if (seen.has(m.id)) return false;
                        seen.add(m.id);
                        return true;
                    });
                });

                setHasMoreMessages(data.length === PAGE_SIZE);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            if (loadMore) {
                loadingMoreMessagesRef.current = false;
                setLoadingMoreMessages(false);
            }
        }
    };

    const fetchAvailableUsers = async () => {
        try {
            const companyId = company?.id || userProfile?.company_id;
            if (!companyId || !userProfile?.id) return;
            setLoadingUsers(true);
            const users = await getAvailableUsers(companyId, userProfile.id);
            setAvailableUsers(users);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleConversationClick = async (conversation) => {
        setSelectedConversation(conversation);
        setHasMoreMessages(true);
        setPendingAttachment(null);
        setShowAttachmentPicker(false);
        setAttachmentMode(null);
        await fetchMessages(conversation.id);

        await markMessagesAsRead(conversation.id, userProfile.id);
        await fetchConversations();
        if (onRefreshBadge) onRefreshBadge();
    };

    const handleScroll = (e) => {
        const { scrollTop } = e.target;
        if (scrollTop <= 20 && hasMoreMessages && !loadingMoreMessagesRef.current && selectedConversation) {
            const prevHeight = messagesContainerRef.current.scrollHeight;
            fetchMessages(selectedConversation.id, true).then(() => {
                requestAnimationFrame(() => {
                    if (messagesContainerRef.current) {
                        messagesContainerRef.current.scrollTop =
                            messagesContainerRef.current.scrollHeight - prevHeight;
                    }
                });
            });
        }
    };

    const toggleGroupMember = (userId) => {
        setSelectedGroupMemberIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleCreateGroupConversation = async () => {
        try {
            if (!groupTitle.trim()) {
                alert('Please enter a group name');
                return;
            }

            if (selectedGroupMemberIds.length === 0) {
                alert('Please select at least one member');
                return;
            }

            setLoadingUsers(true);

            const conversation = await createGroupConversation({
                companyId: company?.id || userProfile?.company_id,
                creatorId: userProfile.id,
                title: groupTitle,
                memberIds: selectedGroupMemberIds,
            });

            setSelectedConversation(conversation);
            setMessages([]);
            setShowNewConversation(false);
            setGroupTitle('');
            setSelectedGroupMemberIds([]);
            setNewConversationMode('direct');

            await fetchConversations();
        } catch (error) {
            console.error('Error creating group:', error);
            alert(error.message || 'Failed to create group');
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleCreateConversation = async (userId) => {
        try {
            setLoadingUsers(true);
            const conversation = await getOrCreateConversation(userProfile.id, userId, company.id);
            setSelectedConversation(conversation);
            await fetchMessages(conversation.id);
            setShowNewConversation(false);
            fetchConversations();
        } catch (error) {
            console.error('Error creating conversation:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleSendMessage = async () => {
        const hasText = messageText.trim();
        const hasAttachment = !!pendingAttachment;
        if ((!hasText && !hasAttachment) || !selectedConversation || isLoading || uploadingFile) return;

        const content = messageText.trim();
        const attachment = pendingAttachment;
        const reply = replyingTo;

        try {
            setIsLoading(true);
            setMessageText('');
            setPendingAttachment(null);
            setShowAttachmentPicker(false);
            setAttachmentMode(null);
            setReplyingTo(null);

            // Reset textarea height
            if (messageInputRef.current) {
                messageInputRef.current.style.height = 'auto';
            }

            // Strip local previewUrl before storing in DB — it's a blob URL, not persistable
            // eslint-disable-next-line no-unused-vars
            const { previewUrl: _preview, ...attachmentForDb } = attachment ?? {};
            const isGroupConversation = selectedConversation.type === 'group';

            const newMessage = await sendMessage(
                selectedConversation.id,
                userProfile.id,
                isGroupConversation ? null : selectedConversation.otherUser.id,
                content,
                attachment ? attachmentForDb : null,
                {
                    isGroup: isGroupConversation,
                    metadata: reply
                        ? {
                            reply_to: reply,
                        }
                        : null,
                }
            );

            setMessages(prev => [...prev, newMessage]);
            bumpConversationToTop(selectedConversation.id, newMessage);
            if (onRefreshBadge) onRefreshBadge();
        } catch (error) {
            console.error('Error sending message:', error);
            setMessageText(content);
            setPendingAttachment(attachment);
            setReplyingTo(reply);
        } finally {
            setIsLoading(false);

            requestAnimationFrame(() => {
                messageInputRef.current?.focus();
                messageInputRef.current?.setSelectionRange(0, 0);
            });
        }
    };

    const handleDeleteConversation = async (conversationId, e) => {
        e.stopPropagation();
        if (!window.confirm('Delete this conversation? This cannot be undone.')) return;

        try {
            await deleteConversation(conversationId, userProfile.id);
            if (selectedConversation?.id === conversationId) {
                setSelectedConversation(null);
                setMessages([]);
            }
            fetchConversations();
        } catch (error) {
            console.error('Error deleting conversation:', error);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleVolumeChange = (val) => {
        setVolume(val);
        localStorage.setItem('messageVolume', val.toString());
    };

    const handleMuteToggle = () => {
        const newMuted = !isMuted;
        setIsMuted(newMuted);
        localStorage.setItem('messageMuted', newMuted.toString());
    };

    const clampPanelPosition = (x, y, width = panelSize.width, height = panelSize.height) => {
        const padding = 12;
        const maxX = window.innerWidth - width - padding;
        const maxY = window.innerHeight - height - padding;

        return {
            x: Math.max(padding, Math.min(x, Math.max(padding, maxX))),
            y: Math.max(padding, Math.min(y, Math.max(padding, maxY))),
        };
    };

    const handleDetachToggle = () => {
        if (pageMode) return;
        if (window.innerWidth < 768) return;

        setIsDetached(prev => {
            const next = !prev;

            if (!prev) {
                const saved = JSON.parse(localStorage.getItem(MESSAGE_PANEL_STORAGE_KEY) || '{}');

                const savedSize = saved?.panelSize;
                const savedPosition = saved?.panelPosition;

                const width = savedSize?.width || Math.min(560, window.innerWidth - 24);
                const height = savedSize?.height || Math.min(720, window.innerHeight - 24);

                setPanelSize({ width, height });

                setPanelPosition(
                    savedPosition
                        ? clampPanelPosition(savedPosition.x, savedPosition.y, width, height)
                        : clampPanelPosition(
                            Math.round((window.innerWidth - width) / 2),
                            Math.round((window.innerHeight - height) / 2),
                            width,
                            height
                        )
                );
            }

            return next;
        });
    };

    const handlePanelPointerDown = (e) => {
        if (!isDetached || pageMode) return;

        const target = e.target;
        if (
            target.closest('button') ||
            target.closest('input') ||
            target.closest('textarea') ||
            target.closest('a')
        ) {
            return;
        }

        setIsDraggingPanel(true);

        dragStartRef.current = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            panelX: panelPosition.x,
            panelY: panelPosition.y,
        };

        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const handlePanelPointerMove = (e) => {
        if (!isDraggingPanel || !dragStartRef.current) return;

        const dx = e.clientX - dragStartRef.current.startX;
        const dy = e.clientY - dragStartRef.current.startY;

        const next = clampPanelPosition(
            dragStartRef.current.panelX + dx,
            dragStartRef.current.panelY + dy
        );

        setPanelPosition(next);
    };

    const handlePanelPointerUp = (e) => {
        setIsDraggingPanel(false);
        dragStartRef.current = null;
        e.currentTarget.releasePointerCapture?.(e.pointerId);
    };

    const handleResizePanel = (e) => {
        if (!isDetached || pageMode) return;

        const rect = e.currentTarget.getBoundingClientRect();

        setPanelSize({
            width: Math.round(rect.width),
            height: Math.round(rect.height),
        });
    };


    const handlePaste = async (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const fileItem = items.find(item => item.kind === 'file');

        if (!fileItem) return;

        const file = fileItem.getAsFile();
        if (!file) return;

        e.preventDefault();

        const previewUrl = URL.createObjectURL(file);

        try {
            setUploadingFile(true);

            const path = await uploadAttachment(file, userProfile.id);

            setPendingAttachment({
                type: file.type?.startsWith('image/') ? 'image' : 'file',
                path,
                previewUrl,
                name: file.name || `pasted-${Date.now()}`,
                size: file.size,
                mimeType: file.type,
            });
        } catch (error) {
            URL.revokeObjectURL(previewUrl);
            console.error('Paste upload error:', error);
            alert('Failed to upload pasted file.');
        } finally {
            setUploadingFile(false);
        }
    };

    const bumpConversationToTop = (conversationId, message) => {
        setConversations(prev => {
            const found = prev.find(conv => conv.id === conversationId);

            if (!found) {
                fetchConversations();
                return prev;
            }

            const updated = {
                ...found,
                lastMessage: message,
                updated_at: message.created_at || new Date().toISOString(),
                last_message_at: message.created_at || new Date().toISOString(),
                unreadCount:
                    message.sender_id === userProfile.id
                        ? found.unreadCount || 0
                        : selectedConversation?.id === conversationId
                            ? found.unreadCount || 0
                            : (found.unreadCount || 0) + 1,
            };

            return [
                updated,
                ...prev.filter(conv => conv.id !== conversationId),
            ];
        });
    };

    // ── Attachment handlers ───────────────────────────────────────────────────

    const openFilePicker = (type, accept) => {
        setFileInputType(type);
        setFileInputAccept(accept);
        setShowAttachmentPicker(false);
        setAttachmentMode(null);
        setTimeout(() => fileInputRef.current?.click(), 50);
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Create a local blob URL for instant preview in the pending chip
        const previewUrl = URL.createObjectURL(file);

        try {
            setUploadingFile(true);
            const path = await uploadAttachment(file, userProfile.id);
            setPendingAttachment({
                type: fileInputType === 'gif' ? 'gif' : fileInputType === 'image' ? 'image' : 'file',
                path,       // private storage path (used to generate signed URLs)
                previewUrl, // local blob URL for the pending preview chip only
                name: file.name,
                size: file.size,
                mimeType: file.type,
            });
        } catch (error) {
            URL.revokeObjectURL(previewUrl);
            console.error('Upload error:', error);
            alert('Failed to upload file. Make sure the "message-attachments" bucket exists in Supabase Storage.');
        } finally {
            setUploadingFile(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleAddUrl = () => {
        const url = urlInput.trim();
        if (!url) return;
        setPendingAttachment({ type: 'url', url });
        setUrlInput('');
        setAttachmentMode(null);
        setShowAttachmentPicker(false);
    };

    const handleJobSearch = useCallback(async (query) => {
        setJobSearchQuery(query);
        if (!query.trim()) { setJobSearchResults([]); return; }
        setJobSearchLoading(true);
        try {
            const companyId = company?.id || userProfile?.company_id;
            if (!companyId) return;

            const results = await searchJobsForLink(companyId, query);
            setJobSearchResults(results);
        } catch (e) {
            console.error(e);
        } finally {
            setJobSearchLoading(false);
        }
    }, [company?.id]);

    const handleSelectJob = (job) => {
        setPendingAttachment({
            type: 'job',
            jobId: job.id,
            jobUniqueId: job.unique_id,
            jobAddress: job.customer_address,
            companySlug: company.slug,
        });
        setJobSearchQuery('');
        setJobSearchResults([]);
        setAttachmentMode(null);
        setShowAttachmentPicker(false);
    };

    const loadGroupMembers = async () => {
        if (!selectedConversation?.id || selectedConversation.type !== 'group') return;

        try {
            setLoadingGroupMembers(true);
            const members = await getConversationMembers(selectedConversation.id);
            setGroupMembers(members);
        } catch (error) {
            console.error('Error loading group members:', error);
            alert('Failed to load group members');
        } finally {
            setLoadingGroupMembers(false);
        }
    };

    const openGroupDetails = async () => {
        setShowGroupDetails(true);
        await loadGroupMembers();

        if (availableUsers.length === 0) {
            fetchAvailableUsers();
        }
    };

    const handleAddSelectedMembers = async () => {
        try {
            if (selectedAddMemberIds.length === 0) return;

            await addGroupMembers({
                conversationId: selectedConversation.id,
                companyId: company?.id || userProfile?.company_id,
                userIds: selectedAddMemberIds,
            });

            const addedNames = availableUsers
                .filter(u => selectedAddMemberIds.includes(u.id))
                .map(u => u.full_name || u.username)
                .join(', ');

            await sendSystemMessage({
                conversationId: selectedConversation.id,
                senderId: userProfile.id,
                action: 'members_added',
                content: `${userProfile.full_name || userProfile.username} added ${addedNames} to the group`,
                metadata: {
                    actor_id: userProfile.id,
                    added_user_ids: selectedAddMemberIds,
                },
            });

            setSelectedAddMemberIds([]);
            await loadGroupMembers();
        } catch (error) {
            console.error('Error adding members:', error);
            alert(error.message || 'Failed to add members');
        }
    };

    const handlePromoteMember = async (member) => {
        await updateGroupMemberRole({
            conversationId: selectedConversation.id,
            userId: member.user_id,
            role: 'admin',
        });

        await sendSystemMessage({
            conversationId: selectedConversation.id,
            senderId: userProfile.id,
            action: 'member_promoted',
            content: `${userProfile.full_name || userProfile.username} made ${getDisplayName(member.user)} an admin`,
            metadata: {
                actor_id: userProfile.id,
                target_user_id: member.user_id,
            },
        });

        await loadGroupMembers();
    };

    const handleDemoteAdmin = async (member) => {
        await updateGroupMemberRole({
            conversationId: selectedConversation.id,
            userId: member.user_id,
            role: 'member',
        });

        await sendSystemMessage({
            conversationId: selectedConversation.id,
            senderId: userProfile.id,
            action: 'member_demoted',
            content: `${userProfile.full_name || userProfile.username} removed admin from ${getDisplayName(member.user)}`,
            metadata: {
                actor_id: userProfile.id,
                target_user_id: member.user_id,
            },
        });

        await loadGroupMembers();
    };

    const handleRemoveMember = async (member) => {
        if (!window.confirm(`Remove ${getDisplayName(member.user)} from this group?`)) return;

        await removeGroupMember({
            conversationId: selectedConversation.id,
            userId: member.user_id,
        });

        await sendSystemMessage({
            conversationId: selectedConversation.id,
            senderId: userProfile.id,
            action: 'member_removed',
            content: `${userProfile.full_name || userProfile.username} removed ${getDisplayName(member.user)} from the group`,
            metadata: {
                actor_id: userProfile.id,
                target_user_id: member.user_id,
            },
        });

        await loadGroupMembers();
    };

    const handleTransferOwnership = async (member) => {
        if (!window.confirm(`Transfer group ownership to ${getDisplayName(member.user)}?`)) return;

        await transferGroupOwnership({
            conversationId: selectedConversation.id,
            currentOwnerId: userProfile.id,
            newOwnerId: member.user_id,
        });

        await loadGroupMembers();
        await fetchConversations();
    };

    const buildReplyPreview = (message) => {
        const sender = getMessageSender(message, selectedConversation);

        return {
            id: message.id,
            sender_id: message.sender_id,
            sender_name: getDisplayName(sender),
            content: message.content || '',
            attachment_type: message.attachments?.type || null,
            attachment_name: message.attachments?.name || null,
        };
    };

    const handleStartReply = (message) => {
        setReplyingTo(buildReplyPreview(message));

        requestAnimationFrame(() => {
            messageInputRef.current?.focus();
        });
    };

    // ── Computed ──────────────────────────────────────────────────────────────

    // const unreadCount = conversations.reduce((sum, c) => sum + c.unreadCount, 0);
    const filteredConversations = conversations
        .filter(conv => {
            const name = getConversationTitle(conv).toLowerCase();
            return name.includes(searchQuery.toLowerCase());
        })
        .sort((a, b) => {
            const dateA =
                a.lastMessage?.created_at ||
                a.last_message_at ||
                a.updated_at ||
                a.created_at;

            const dateB =
                b.lastMessage?.created_at ||
                b.last_message_at ||
                b.updated_at ||
                b.created_at;

            return new Date(dateB || 0) - new Date(dateA || 0);
        });

    const currentMembership = selectedConversation?.membership || null;
    const currentGroupRole = currentMembership?.role || 'member';

    const canManageGroup =
        selectedConversation?.type === 'group' &&
        ['owner', 'admin'].includes(currentGroupRole);

    const isGroupOwner =
        selectedConversation?.type === 'group' &&
        currentGroupRole === 'owner';

    const attachmentPickerOptions = [
        { key: 'image', label: 'Image', icon: FiImage, accept: 'image/jpeg,image/jpg,image/png,image/webp,image/heic' },
        { key: 'gif', label: 'GIF', icon: FiImage, accept: 'image/gif' },
        { key: 'file', label: 'File', icon: FiFile, accept: '*/*' },
        { key: 'url', label: 'URL', icon: FiLink, accept: null },
        { key: 'job', label: 'Job', icon: FiPackage, accept: null },
    ];

    const isSystemConversation =
        selectedConversation?.otherUser?.role === 'system' ||
        selectedConversation?.otherUser?.username === 'Notify Team';

    if (!isOpen && !pageMode) return null;

    return (
        <>
            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept={fileInputAccept}
                className="hidden"
                onChange={handleFileSelect}
            />

            {/* Image lightbox */}
            {lightboxImage && (
                <div
                    className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
                    onClick={() => setLightboxImage(null)}
                >
                    <button
                        className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-full transition-colors"
                        onClick={() => setLightboxImage(null)}
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                    <img
                        src={lightboxImage}
                        alt="Full size"
                        className="max-w-full max-h-full object-contain rounded-lg"
                        onClick={e => e.stopPropagation()}
                    />
                </div>
            )}

            {/* Backdrop */}
            {!pageMode && !isDetached && (
                <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
            )}

            {/* Panel */}
            <div
                ref={menuRef}
                onPointerMove={handlePanelPointerMove}
                onPointerUp={handlePanelPointerUp}
                onPointerCancel={handlePanelPointerUp}
                onMouseUp={handleResizePanel}
                onPointerLeave={handleResizePanel}
                className={`
                    bg-white dark:bg-zinc-800 overflow-hidden flex flex-col
                    ${pageMode
                        ? 'relative w-full h-full rounded-none border-0 shadow-none'
                        : isDetached
                            ? 'fixed z-50 shadow-2xl rounded-2xl border border-gray-200 dark:border-zinc-700'
                            : 'fixed z-50 shadow-2xl md:bottom-4 md:w-[440px] md:h-[600px] md:rounded-xl md:border md:border-gray-200 md:dark:border-zinc-700 max-md:inset-0 max-md:w-full max-md:h-full max-md:rounded-none'
                    }
                `}
                style={
                    pageMode
                        ? undefined
                        : isDetached
                            ? {
                                left: panelPosition.x,
                                top: panelPosition.y,
                                width: panelSize.width,
                                height: panelSize.height,
                                resize: 'both',
                                minWidth: 380,
                                minHeight: 460,
                                maxWidth: 'calc(100vw - 24px)',
                                maxHeight: 'calc(100vh - 24px)',
                            }
                            : {
                                left: window.innerWidth >= 768
                                    ? (isSidebarOpen ? 'calc(16rem + 1rem)' : 'calc(5.5rem + 0.5rem)')
                                    : undefined,
                            }
                }
            >
                {/* ── Header ── */}
                <div
                    onPointerDown={handlePanelPointerDown}
                    className={`flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-zinc-700 bg-gradient-to-r from-blue-50/80 to-blue-500/20 dark:from-blue-900/20 dark:to-pink-900/20 flex-shrink-0 ${isDetached && !pageMode ? 'cursor-move select-none' : ''
                        }`}
                >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        {selectedConversation && (
                            <button
                                onClick={() => { setSelectedConversation(null); setMessages([]); }}
                                className="p-2 hover:bg-white/70 dark:hover:bg-zinc-700 rounded-lg transition-colors touch-manipulation"
                            >
                                <FiArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            </button>
                        )}

                        {selectedConversation ? (
                            selectedConversation.type === 'group' ? (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-teal-400 flex items-center justify-center text-white flex-shrink-0 shadow-sm">
                                    <HiMiniUserGroup className="w-5 h-5" />
                                </div>
                            ) : selectedConversation.otherUser?.avatar_url &&
                                selectedConversation.otherUser.role !== 'system' ? (
                                <img
                                    src={selectedConversation.otherUser.avatar_url}
                                    alt={getDisplayName(selectedConversation.otherUser)}
                                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                />
                            ) : (
                                getInitialAvatar(selectedConversation.otherUser, 'sm')
                            )
                        ) : (
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex-shrink-0">
                                <FiMessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                        )}

                        <div className="flex flex-col gap-0 min-w-0">
                            <h3 className="font-semibold mb-0 text-gray-900 dark:text-white text-base truncate leading-tight">
                                {selectedConversation ? getConversationTitle(selectedConversation) : 'Messages'}
                            </h3>
                            {selectedConversation?.type === 'group' && (
                                <button
                                    type="button"
                                    onClick={openGroupDetails}
                                    className="text-xs text-blue-600 dark:text-blue-300 font-medium hover:underline self-start"
                                >
                                    Group details
                                </button>
                            )}
                            {!selectedConversation && unreadCount > 0 && (
                                <span className="text-xs text-blue-600 dark:text-blue-300 font-medium">
                                    {unreadCount} unread
                                </span>
                            )}
                        </div>
                    </div>
                    {!pageMode && (
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/70 dark:hover:bg-zinc-700 rounded-xl transition-colors flex-shrink-0 touch-manipulation"
                        >
                            <FiX className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        </button>
                    )}

                    {!pageMode && (
                        <button
                            onClick={() => navigate(`/${company.slug}/messages`)}
                            className="p-2 hover:bg-white/70 dark:hover:bg-zinc-700 rounded-xl transition-colors flex-shrink-0 touch-manipulation"
                            title="Open full page"
                        >
                            <FiMaximize2 className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        </button>
                    )}

                    {!pageMode && (
                        <button
                            onClick={handleDetachToggle}
                            className="p-2 hover:bg-white/70 dark:hover:bg-zinc-700 rounded-xl transition-colors flex-shrink-0 touch-manipulation"
                            title={isDetached ? 'Dock message box' : 'Release message box'}
                        >
                            {isDetached ? (
                                <FiMinimize2 className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                            ) : (
                                <FiMove className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                            )}
                        </button>
                    )}
                </div>

                {/* ── Conversation list controls ── */}
                {!selectedConversation && (
                    <div className="flex flex-col p-3 bg-white/70 dark:bg-zinc-900/50 border-b border-gray-200 dark:border-zinc-700 flex-shrink-0">
                        {/* Volume */}
                        <div className={`flex items-center gap-2 mb-3 ${pageMode ? "w-64 self-end" : "w-unset self-start"}`}>
                            <button
                                onClick={handleMuteToggle}
                                className={`p-2.5 rounded-lg transition-all touch-manipulation ${isMuted
                                    ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                                    : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                                    }`}
                            >
                                {isMuted ? <FiVolumeX className="w-5 h-5" /> : <FiVolume2 className="w-5 h-5" />}
                            </button>
                            {!isMuted && (
                                <div className="flex items-center gap-2 flex-1">
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        value={volume}
                                        onChange={e => handleVolumeChange(parseFloat(e.target.value))}
                                        className="flex-1 h-2 bg-gray-200 dark:bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                                    />
                                    <button
                                        onClick={() => messageSound.testSound()}
                                        className="px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors whitespace-nowrap"
                                    >
                                        Test
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Search + New */}
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Search conversations..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="pl-9 pr-3 py-2.5 text-sm border border-gray-200 dark:border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full bg-white dark:bg-zinc-900 dark:text-white"
                                />
                            </div>
                            <button
                                onClick={() => { setShowNewConversation(true); fetchAvailableUsers(); }}
                                className="px-4 py-2.5 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 rounded-lg transition-colors flex items-center gap-2 flex-shrink-0 touch-manipulation"
                            >
                                <FiMessageSquare className="w-4 h-4" />
                                <span className="hidden sm:inline">New</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Content ── */}
                <div className="flex-1 overflow-hidden">
                    {!selectedConversation ? (
                        /* ── Conversations list ── */
                        <div className="h-full overflow-y-auto">
                            {filteredConversations.length === 0 ? (
                                <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-zinc-700 rounded-2xl flex items-center justify-center">
                                        <FiMessageSquare className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <p className="text-sm font-medium mb-1">No messages yet</p>
                                    <p className="text-xs text-gray-400 leading-relaxed">
                                        Start a conversation with your team members
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100 dark:divide-zinc-700">
                                    {filteredConversations.map(conv => (
                                        <div
                                            key={conv.id}
                                            className={`px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-zinc-700/50 active:bg-gray-100 dark:active:bg-zinc-700 cursor-pointer transition-all group touch-manipulation ${conv.unreadCount > 0 ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''}`}
                                            onClick={() => handleConversationClick(conv)}
                                        >
                                            <div className="flex items-center gap-3">
                                                {/* Avatar */}
                                                <div className="relative flex-shrink-0">
                                                    {conv.type === 'group' ? (
                                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-teal-400 flex items-center justify-center text-white shadow-sm">
                                                            <HiMiniUserGroup className="w-6 h-6" />
                                                        </div>
                                                    ) : conv.otherUser?.avatar_url && conv.otherUser.role !== 'system' ? (
                                                        <img
                                                            src={conv.otherUser.avatar_url}
                                                            alt={getConversationTitle(conv)}
                                                            className="w-12 h-12 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        getInitialAvatar(conv.otherUser)
                                                    )}

                                                    {conv.unreadCount > 0 && (
                                                        <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-blue-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                                            {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between mb-0.5">
                                                        <h4 className={`text-sm font-semibold truncate ${conv.unreadCount > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                                                            {getConversationTitle(conv)}
                                                        </h4>
                                                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                                                            <span className="text-xs text-gray-400">
                                                                {(conv.lastMessage?.created_at || conv.last_message_at || conv.updated_at)
                                                                    ? formatTimestamp(conv.lastMessage?.created_at || conv.last_message_at || conv.updated_at)
                                                                    : ''}
                                                            </span>
                                                            <button
                                                                onClick={e => handleDeleteConversation(conv.id, e)}
                                                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-all"
                                                                title="Delete conversation"
                                                            >
                                                                <FiX className="w-3 h-3 text-red-400" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <p className={`text-xs truncate max-w-[260px] ${conv.unreadCount > 0 ? 'text-gray-700 dark:text-gray-300 font-medium' : 'text-gray-400 dark:text-gray-500'}`}>
                                                        {conv.lastMessage
                                                            ? conv.lastMessage.attachments
                                                                ? `${conv.lastMessage.content ? conv.lastMessage.content + ' · ' : ''}📎 Attachment`
                                                                : conv.lastMessage.content
                                                            : 'No messages yet'
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        /* ── Message view ── */
                        <div className="h-full flex flex-col">
                            {/* Messages scroll area */}
                            <div
                                ref={messagesContainerRef}
                                onScroll={handleScroll}
                                className="flex-1 overflow-y-auto px-4 py-3 overscroll-contain space-y-2"
                            >
                                {/* Load more spinner */}
                                {loadingMoreMessages && (
                                    <div className="flex justify-center py-3">
                                        <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                )}

                                {/* Beginning of conversation */}
                                {!hasMoreMessages && messages.length > 0 && (
                                    <div className="flex justify-center py-3">
                                        <span className="text-xs text-gray-400 bg-gray-100 dark:bg-zinc-700 px-3 py-1 rounded-full">
                                            Beginning of conversation
                                        </span>
                                    </div>
                                )}

                                {/* Messages */}
                                {messages.length === 0 ? (
                                    <div className="flex items-center justify-center h-full text-gray-400">
                                        <div className="text-center">
                                            <FiMessageSquare className="w-12 h-12 mx-auto mb-3 opacity-40" />
                                            <p className="text-sm font-medium">No messages yet</p>
                                            <p className="text-xs mt-1 opacity-70">Send a message to start the conversation</p>
                                        </div>
                                    </div>
                                ) : (
                                    messages.map((message, index) => {
                                        const isOwn = message.sender_id === userProfile.id;

                                        const sender = getMessageSender(message, selectedConversation);
                                        const showSenderName =
                                            selectedConversation?.type === 'group' &&
                                            !isOwn &&
                                            message.message_type !== 'system' &&
                                            (index === 0 || messages[index - 1].sender_id !== message.sender_id);

                                        const showDateSep = index === 0 || !isSameDay(message.created_at, messages[index - 1].created_at);
                                        // Group consecutive messages from same sender (show avatar only on last in group)
                                        const isLastInGroup = index === messages.length - 1 || messages[index + 1].sender_id !== message.sender_id;
                                        return (
                                            <React.Fragment key={message.id}>

                                                {message.message_type === 'system' && (
                                                    <div className="flex justify-center my-3">
                                                        <div className="max-w-[70%] rounded-lg bg-gray-100 dark:bg-zinc-700 px-3 py-1.5 text-center">
                                                            <p className="text-[11px] font-medium text-gray-500 dark:text-gray-300">
                                                                {message.content}
                                                            </p>
                                                            <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300">
                                                                {formatMessageTime(message.created_at)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {message.message_type !== 'system' && (
                                                    <>


                                                        {/* Date separator */}
                                                        {showDateSep && (
                                                            <div className="flex items-center justify-center my-4">
                                                                <span className="text-xs text-gray-400 bg-gray-100 dark:bg-zinc-700 px-3 py-1 rounded-full select-none">
                                                                    {getDateLabel(message.created_at)}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* Message bubble */}
                                                        <div className={`flex items-end gap-2 mb-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                                                            {/* Avatar for incoming (last in group only) */}
                                                            {!isOwn && (
                                                                <div className={`flex-shrink-0 ${isLastInGroup ? 'opacity-100' : 'opacity-0'}`}>
                                                                    {(() => {
                                                                        const sender = getMessageSender(message, selectedConversation);

                                                                        return sender?.avatar_url && sender.role !== 'system' ? (
                                                                            <img
                                                                                src={sender.avatar_url}
                                                                                alt=""
                                                                                className="w-6 h-6 rounded-full object-cover"
                                                                            />
                                                                        ) : (
                                                                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-600 to-teal-300 flex items-center justify-center text-white text-[10px] font-bold">
                                                                                {getDisplayName(sender).charAt(0).toUpperCase()}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}

                                                            <div className={`relative group/message flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-[78%]`}>


                                                                <div className={`px-3.5 py-2 rounded-2xl ${isOwn
                                                                    ? 'bg-blue-200/70 text-black rounded-br-sm'
                                                                    : 'bg-gray-100 dark:bg-zinc-700 text-gray-900 dark:text-white rounded-bl-sm'
                                                                    }`}>
                                                                    {showSenderName && (
                                                                        <p className="mb-[0rem] text-[12px] font-bold text-blue-800 dark:text-gray-400">
                                                                            {getDisplayName(sender)}
                                                                        </p>
                                                                    )}

                                                                    {message.metadata?.reply_to && (
                                                                        <div className={`
        mb-1.5 rounded-lg border-l-4 px-2.5 py-1.5 text-xs max-w-[240px]
        ${isOwn
                                                                                ? 'bg-blue-100/70 border-blue-500 text-blue-900'
                                                                                : 'bg-white/70 dark:bg-zinc-800/70 border-gray-400 text-gray-700 dark:text-gray-200'
                                                                            }
    `}>
                                                                            <p className="font-bold truncate">
                                                                                {message.metadata.reply_to.sender_name}
                                                                            </p>

                                                                            <p className="truncate opacity-80">
                                                                                {message.metadata.reply_to.content
                                                                                    ? message.metadata.reply_to.content
                                                                                    : message.metadata.reply_to.attachment_type
                                                                                        ? `Attachment: ${message.metadata.reply_to.attachment_name || message.metadata.reply_to.attachment_type}`
                                                                                        : 'Message'
                                                                                }
                                                                            </p>
                                                                        </div>
                                                                    )}

                                                                    {/* Attachment */}
                                                                    {message.attachments && (
                                                                        <AttachmentMessage
                                                                            attachment={message.attachments}
                                                                            navigate={navigate}
                                                                            onImageClick={setLightboxImage}
                                                                            isOwn={isOwn}
                                                                        />
                                                                    )}
                                                                    {/* Text */}
                                                                    {message.content && (
                                                                        <div className={message.attachments ? 'mt-1.5' : ''}>
                                                                            <MessageTextWithLinks text={message.content} isOwn={isOwn} />
                                                                            <UrlPreviewCard text={message.content} />
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <MessageReactions
                                                                    messageId={message.id}
                                                                    currentUserId={userProfile.id}
                                                                    isOwn={isOwn}
                                                                    onReply={() => handleStartReply(message)}
                                                                />


                                                                {/* Time + ticks */}
                                                                <div className="flex items-center gap-1 mt-0.5 px-1">
                                                                    <span className="text-[11px] text-gray-400 select-none">
                                                                        {formatMessageTime(message.created_at)}
                                                                    </span>
                                                                    {isOwn && <DoubleCheck isRead={message.is_read} />}
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* ── Input area ── */}
                            <div className="flex-shrink-0 border-t border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
                                {isSystemConversation ? (
                                    <div className="p-4">
                                        <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                                            <FiMessageSquare className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                                            <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                                                System message — replies not available.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-3">
                                        {/* Attachment picker */}
                                        {showAttachmentPicker && (
                                            <div className="mb-2 p-2 bg-gray-50 dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-700">
                                                {/* Option buttons */}
                                                <div className="flex items-stretch gap-1">
                                                    {attachmentPickerOptions.map(({ key, label, icon: Icon, accept }) => (
                                                        <button
                                                            key={key}
                                                            onClick={() => {
                                                                if (accept !== null) {
                                                                    openFilePicker(key, accept);
                                                                } else {
                                                                    setAttachmentMode(attachmentMode === key ? null : key);
                                                                }
                                                            }}
                                                            className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg text-xs font-medium transition-colors ${attachmentMode === key
                                                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                                                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800'
                                                                }`}
                                                        >
                                                            <Icon className="w-4 h-4" />
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>

                                                {/* URL input */}
                                                {attachmentMode === 'url' && (
                                                    <div className="flex gap-2 mt-2">
                                                        <input
                                                            type="url"
                                                            placeholder="https://..."
                                                            value={urlInput}
                                                            onChange={e => setUrlInput(e.target.value)}
                                                            onKeyDown={e => { if (e.key === 'Enter') handleAddUrl(); }}
                                                            className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-zinc-800 dark:text-white"
                                                            autoFocus
                                                        />
                                                        <button
                                                            onClick={handleAddUrl}
                                                            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                                        >
                                                            Add
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Job search */}
                                                {attachmentMode === 'job' && (
                                                    <div className="mt-2">
                                                        <input
                                                            type="text"
                                                            placeholder="Search by job ID or address…"
                                                            value={jobSearchQuery}
                                                            onChange={e => handleJobSearch(e.target.value)}
                                                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-zinc-800 dark:text-white"
                                                            autoFocus
                                                        />
                                                        {jobSearchLoading && (
                                                            <div className="flex justify-center py-2">
                                                                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                                                            </div>
                                                        )}
                                                        {jobSearchResults.length > 0 && (
                                                            <div className="mt-1 max-h-[120px] overflow-y-auto rounded-lg border border-gray-200 dark:border-zinc-600 bg-white dark:bg-zinc-800">
                                                                {jobSearchResults.map(job => (
                                                                    <button
                                                                        key={job.id}
                                                                        onClick={() => handleSelectJob(job)}
                                                                        className="w-full px-3 py-2 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border-b border-gray-100 dark:border-zinc-700 last:border-0"
                                                                    >
                                                                        <p className="text-xs font-semibold text-gray-900 dark:text-white">#{job.unique_id}</p>
                                                                        {job.customer_address && (
                                                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{job.customer_address}</p>
                                                                        )}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {!jobSearchLoading && jobSearchQuery && jobSearchResults.length === 0 && (
                                                            <p className="text-xs text-gray-400 text-center py-2">No jobs found</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Upload progress */}
                                        {uploadingFile && (
                                            <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                                <span className="text-xs text-blue-600 dark:text-blue-300 font-medium">Uploading…</span>
                                            </div>
                                        )}

                                        {/* Pending attachment preview */}
                                        {pendingAttachment && !uploadingFile && (
                                            <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                                {(pendingAttachment.type === 'image' || pendingAttachment.type === 'gif') ? (
                                                    <img
                                                        src={pendingAttachment.previewUrl}
                                                        alt="preview"
                                                        className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                                                    />
                                                ) : pendingAttachment.type === 'file' ? (
                                                    <FiFile className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                ) : pendingAttachment.type === 'url' ? (
                                                    <FiLink className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                ) : (
                                                    <FiPackage className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                )}
                                                <span className="text-xs text-blue-700 dark:text-blue-300 truncate flex-1">
                                                    {pendingAttachment.type === 'job'
                                                        ? `Job #${pendingAttachment.jobUniqueId}${pendingAttachment.jobAddress ? ' · ' + pendingAttachment.jobAddress : ''}`
                                                        : pendingAttachment.type === 'url'
                                                            ? pendingAttachment.url
                                                            : pendingAttachment.name
                                                    }
                                                </span>
                                                <button
                                                    onClick={() => setPendingAttachment(null)}
                                                    className="p-0.5 text-blue-400 hover:text-blue-600 transition-colors flex-shrink-0"
                                                >
                                                    <FiX className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}

                                        {replyingTo && (
                                            <div className="mb-2 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-800 dark:bg-blue-900/20">
                                                <div className="w-1 self-stretch rounded-full bg-blue-500" />

                                                <div className="min-w-0 flex-1">
                                                    <p className="text-xs font-bold text-blue-700 dark:text-blue-300">
                                                        Replying to {replyingTo.sender_name}
                                                    </p>

                                                    <p className="text-xs text-blue-700/80 dark:text-blue-200/80 truncate">
                                                        {replyingTo.content
                                                            ? replyingTo.content
                                                            : replyingTo.attachment_type
                                                                ? `Attachment: ${replyingTo.attachment_name || replyingTo.attachment_type}`
                                                                : 'Message'
                                                        }
                                                    </p>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => setReplyingTo(null)}
                                                    className="p-1 text-blue-500 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-100"
                                                >
                                                    <FiX className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}

                                        {/* Input row */}
                                        <div className="flex items-end gap-2">
                                            {/* Attachment toggle */}
                                            <button
                                                onClick={() => {
                                                    setShowAttachmentPicker(!showAttachmentPicker);
                                                    if (showAttachmentPicker) setAttachmentMode(null);
                                                }}
                                                className={`p-3 rounded-xl transition-all flex-shrink-0 touch-manipulation ${showAttachmentPicker
                                                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                                                    : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-700 dark:text-gray-500'
                                                    }`}
                                            >
                                                <FiPlus className={`w-5 h-5 transition-transform duration-200 ${showAttachmentPicker ? 'rotate-45' : ''}`} />
                                            </button>

                                            {/* Textarea */}
                                            <textarea
                                                ref={messageInputRef}
                                                value={messageText}
                                                onChange={e => {
                                                    setMessageText(e.target.value);
                                                    autoResizeTextarea(e.target);
                                                }}
                                                onKeyDown={handleKeyDown}
                                                onPaste={handlePaste}
                                                placeholder="Type a message…"
                                                rows={1}
                                                disabled={isLoading || uploadingFile}
                                                className="flex-1 px-4 py-3 text-sm border border-gray-200 dark:border-zinc-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none max-h-32 bg-white dark:bg-zinc-900 dark:text-white disabled:opacity-60 touch-manipulation leading-relaxed"
                                                style={{ minHeight: '44px' }}
                                            />

                                            {/* Send */}
                                            <button
                                                onClick={handleSendMessage}
                                                disabled={(!messageText.trim() && !pendingAttachment) || isLoading || uploadingFile}
                                                className={`p-3 rounded-xl transition-all touch-manipulation flex-shrink-0 ${(messageText.trim() || pendingAttachment) && !isLoading && !uploadingFile
                                                    ? 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700 shadow-lg'
                                                    : 'bg-gray-100 dark:bg-zinc-700 text-gray-400 cursor-not-allowed'
                                                    }`}
                                            >
                                                {isLoading ? (
                                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    <FiSend className="w-5 h-5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* ── New Conversation overlay ── */}
                {showNewConversation && (
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm z-10 flex items-center justify-center p-0 md:p-6">
                        <div className="bg-white dark:bg-zinc-800 rounded-none md:rounded-2xl shadow-2xl w-full h-full md:h-[500px] flex flex-col">
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-700 flex-shrink-0">
                                <div>
                                    <h3 className="font-semibold text-base text-gray-900 dark:text-white">
                                        New Conversation
                                    </h3>

                                    <div className="flex items-center gap-1 mt-2 bg-gray-100 dark:bg-zinc-900 rounded-lg p-1">
                                        <button
                                            type="button"
                                            onClick={() => setNewConversationMode('direct')}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${newConversationMode === 'direct'
                                                ? 'bg-white dark:bg-zinc-700 text-gray-900 dark:text-white shadow-sm'
                                                : 'text-gray-500'
                                                }`}
                                        >
                                            Direct
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => setNewConversationMode('group')}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${newConversationMode === 'group'
                                                ? 'bg-white dark:bg-zinc-700 text-gray-900 dark:text-white shadow-sm'
                                                : 'text-gray-500'
                                                }`}
                                        >
                                            Group
                                        </button>
                                    </div>
                                </div>
                                <button
                                    onClick={() => { setShowNewConversation(false); setSearchUsers(''); }}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-700 rounded-lg transition-colors touch-manipulation"
                                >
                                    <FiX className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                                </button>
                            </div>

                            {/* Search */}
                            <div className="p-4 border-b border-gray-200 dark:border-zinc-700 flex-shrink-0">
                                <div className="relative">
                                    <FiSearch className="absolute left-3 top-6 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                    <input
                                        type="text"
                                        placeholder="Search users…"
                                        value={searchUsers}
                                        onChange={e => setSearchUsers(e.target.value)}
                                        className="w-full pl-9 pr-3 py-3 text-sm border border-gray-200 dark:border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-zinc-900 dark:text-white"
                                        autoFocus
                                    />
                                    {newConversationMode === 'group' && (
                                        <div className="mt-3">
                                            <input
                                                type="text"
                                                placeholder="Group name..."
                                                value={groupTitle}
                                                onChange={e => setGroupTitle(e.target.value)}
                                                className="w-full px-3 py-3 text-sm border border-gray-200 dark:border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-zinc-900 dark:text-white"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Users list */}
                            <div className="flex-1 overflow-y-auto">
                                {loadingUsers ? (
                                    <div className="p-12 text-center">
                                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading users…</p>
                                    </div>
                                ) : (
                                    availableUsers
                                        .filter(u =>
                                            (u.full_name || u.username || '').toLowerCase().includes(searchUsers.toLowerCase())
                                        )
                                        .length === 0 ? (
                                        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                                            <p className="text-sm">No users found</p>
                                        </div>
                                    ) : (
                                        availableUsers
                                            .filter(u =>
                                                (u.full_name || u.username || '').toLowerCase().includes(searchUsers.toLowerCase())
                                            )
                                            .map(user => (
                                                <div
                                                    key={user.id}
                                                    onClick={() => {
                                                        if (newConversationMode === 'group') {
                                                            toggleGroupMember(user.id);
                                                        } else {
                                                            handleCreateConversation(user.id);
                                                        }
                                                    }}
                                                    className="px-4 py-3.5 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 active:bg-blue-100/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-zinc-700 last:border-0 touch-manipulation"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        {user.avatar_url ? (
                                                            <img
                                                                src={user.avatar_url}
                                                                alt={user.username}
                                                                className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                                                            />
                                                        ) : (
                                                            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gray-400 to-zinc-600 flex items-center justify-center text-white font-semibold text-base flex-shrink-0">
                                                                {(user.full_name || user.username || '?').charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                                {capitalizeFirstLetter(user.full_name || user.username)}
                                                            </h4>
                                                            <span className={`text-xs px-2 py-0.5 rounded-full inline-block mt-0.5 ${getRoleBadgeColor(user.role)}`}>
                                                                {user.role.replace(/_/g, ' ').toUpperCase()}
                                                            </span>
                                                        </div>
                                                        {newConversationMode === 'group' && (
                                                            <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${selectedGroupMemberIds.includes(user.id)
                                                                ? 'bg-blue-600 border-blue-600'
                                                                : 'bg-white border-gray-300 dark:bg-zinc-900 dark:border-zinc-600'
                                                                }`}>
                                                                {selectedGroupMemberIds.includes(user.id) && (
                                                                    <FiCheck className="w-3.5 h-3.5 text-white" />
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                    )
                                )}
                            </div>

                            {newConversationMode === 'group' && (
                                <div className="p-4 border-t border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
                                    <button
                                        type="button"
                                        onClick={handleCreateGroupConversation}
                                        disabled={loadingUsers || !groupTitle.trim() || selectedGroupMemberIds.length === 0}
                                        className="w-full px-4 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Create Group ({selectedGroupMemberIds.length + 1})
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {showGroupDetails && selectedConversation?.type === 'group' && (
                <div className="mobile-dialog-shell fixed inset-0 bg-black/40 z-[110] flex items-end md:items-center justify-center p-0 md:p-6">
                    <div className="bg-white dark:bg-zinc-800 w-full md:max-w-lg h-[85vh] md:h-[680px] rounded-t-3xl md:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                        <div className="p-4 border-b border-gray-200 dark:border-zinc-700 flex items-center justify-between">
                            <div>
                                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                                    {getConversationTitle(selectedConversation)}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Members, admins and ownership
                                </p>
                            </div>

                            <button
                                onClick={() => setShowGroupDetails(false)}
                                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-zinc-700"
                            >
                                <FiX className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {canManageGroup && (
                                <div className="rounded-2xl border border-gray-200 dark:border-zinc-700 p-3">
                                    <p className="text-xs font-bold text-gray-500 uppercase mb-2">
                                        Add members
                                    </p>

                                    <div className="max-h-40 overflow-y-auto space-y-1">
                                        {availableUsers
                                            .filter(u => !groupMembers.some(m => m.user_id === u.id))
                                            .map(user => {
                                                const checked = selectedAddMemberIds.includes(user.id);

                                                return (
                                                    <button
                                                        key={user.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedAddMemberIds(prev =>
                                                                checked
                                                                    ? prev.filter(id => id !== user.id)
                                                                    : [...prev, user.id]
                                                            );
                                                        }}
                                                        className="w-full flex items-center gap-2 p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-zinc-700 text-left"
                                                    >
                                                        {user.avatar_url ? (
                                                            <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                                                        ) : (
                                                            <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
                                                                {(user.full_name || user.username || '?').charAt(0).toUpperCase()}
                                                            </div>
                                                        )}

                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                                                                {user.full_name || user.username}
                                                            </p>
                                                            <p className="text-xs text-gray-400 truncate">
                                                                {user.role?.replace(/_/g, ' ')}
                                                            </p>
                                                        </div>

                                                        <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                                            }`}>
                                                            {checked && <FiCheck className="w-3.5 h-3.5 text-white" />}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                    </div>

                                    <button
                                        type="button"
                                        disabled={selectedAddMemberIds.length === 0}
                                        onClick={handleAddSelectedMembers}
                                        className="mt-3 w-full px-3 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        Add selected ({selectedAddMemberIds.length})
                                    </button>
                                </div>
                            )}

                            <div>
                                <p className="text-xs font-bold text-gray-500 uppercase mb-2">
                                    Members
                                </p>

                                {loadingGroupMembers ? (
                                    <div className="p-6 text-center text-sm text-gray-400">
                                        Loading members...
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {groupMembers.map(member => {
                                            const isSelf = member.user_id === userProfile.id;
                                            const isOwnerMember = member.role === 'owner';
                                            const isAdminMember = member.role === 'admin';

                                            return (
                                                <div
                                                    key={member.id}
                                                    className="flex items-center gap-3 p-3 rounded-2xl border border-gray-200 dark:border-zinc-700"
                                                >
                                                    {member.user?.avatar_url ? (
                                                        <img src={member.user.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                                                            {getDisplayName(member.user).charAt(0).toUpperCase()}
                                                        </div>
                                                    )}

                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                            {getDisplayName(member.user)} {isSelf ? '(You)' : ''}
                                                        </p>
                                                        <p className="text-xs text-gray-400 capitalize">
                                                            {member.role}
                                                        </p>
                                                    </div>

                                                    {!isSelf && canManageGroup && !isOwnerMember && (
                                                        <div className="flex items-center gap-1">
                                                            {isGroupOwner && member.role === 'member' && (
                                                                <button
                                                                    onClick={() => handlePromoteMember(member)}
                                                                    className="px-2 py-1 text-[11px] rounded-lg bg-blue-50 text-blue-600 font-semibold"
                                                                >
                                                                    Make admin
                                                                </button>
                                                            )}

                                                            {isGroupOwner && isAdminMember && (
                                                                <button
                                                                    onClick={() => handleDemoteAdmin(member)}
                                                                    className="px-2 py-1 text-[11px] rounded-lg bg-gray-100 text-gray-600 font-semibold"
                                                                >
                                                                    Demote
                                                                </button>
                                                            )}

                                                            {isGroupOwner && (
                                                                <button
                                                                    onClick={() => handleTransferOwnership(member)}
                                                                    className="px-2 py-1 text-[11px] rounded-lg bg-amber-50 text-amber-700 font-semibold"
                                                                >
                                                                    Transfer
                                                                </button>
                                                            )}

                                                            <button
                                                                onClick={() => handleRemoveMember(member)}
                                                                className="px-2 py-1 text-[11px] rounded-lg bg-red-50 text-red-600 font-semibold"
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MessageMenu;
